import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import maplibregl, { type Map as MlMap } from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { latLngToCell } from 'h3-js';
import type { FeatureCollection, Polygon, MultiPolygon, Feature } from 'geojson';
import { config } from '../config';
import { useGeolocation } from '../hooks/useGeolocation';
import { useContainingCity } from '../hooks/useContainingCity';
import { fetchVisitedCellsInBbox, type Bbox } from '../api/cells';
import { fetchCitiesInBbox, type CityWithBoundary } from '../api/cities';
import { fetchLifetimeStats } from '../api/stats';
import { fetchAllProgress } from '../api/progress';
import { cellsToFeatureCollection } from '../map/h3';
import { useRealtimeCells } from '../realtime/useRealtimeCells';
import { ConnectionPill } from '../ui/ConnectionPill';
import { LocationPermissionPrompt } from '../ui/LocationPermissionPrompt';
import { MapModeButton, type MapMode } from '../ui/MapModeButton';
import { CityLabel } from '../ui/CityLabel';
import './MapScreen.css';

const CELL_SOURCE = 'visited-cells';
const PENDING_SOURCE = 'pending-cells';
const CITY_SOURCE = 'city-boundaries';
const PENDING_STORAGE_KEY = 'cabin.pending_cells.v1';
const H3_RES = 9;
const DEFAULT_CENTER: [number, number] = [-71.0589, 42.3601]; // Boston fallback
const DEFAULT_ZOOM = 13;
const VIEWPORT_DEBOUNCE_MS = 400;
const FOLLOW_DEFAULT_ZOOM = 14;
const CITY_FIT_PADDING = 60;

const EMPTY_FC: FeatureCollection<Polygon | MultiPolygon> = {
  type: 'FeatureCollection',
  features: [],
};

function loadPendingFromStorage(): Set<string> {
  try {
    const raw = localStorage.getItem(PENDING_STORAGE_KEY);
    if (!raw) return new Set();
    const arr = JSON.parse(raw);
    if (!Array.isArray(arr)) return new Set();
    return new Set(arr.filter((x): x is string => typeof x === 'string'));
  } catch {
    return new Set();
  }
}

function savePendingToStorage(set: Set<string>): void {
  try {
    localStorage.setItem(PENDING_STORAGE_KEY, JSON.stringify(Array.from(set)));
  } catch {
    // localStorage full or disabled — pending will live in memory only
  }
}

export function MapScreen() {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<MlMap | null>(null);
  const cellsRef = useRef<Set<string>>(new Set());
  const pendingCellsRef = useRef<Set<string>>(loadPendingFromStorage());
  const citiesRef = useRef<CityWithBoundary[]>([]);
  const [mapReady, setMapReady] = useState(false);
  const [bbox, setBbox] = useState<Bbox | null>(null);
  const [reconnectVersion, setReconnectVersion] = useState(0);
  const wasConnectedRef = useRef(false);
  const geo = useGeolocation();

  // Camera mode (button cycles overview → city → follow → overview).
  // Default to follow so first GPS fix snaps the user onto themselves.
  const [mapMode, setMapMode] = useState<MapMode>('follow');
  const mapModeRef = useRef<MapMode>('follow');
  const userZoomOverrideRef = useRef<number | null>(null);
  useEffect(() => {
    mapModeRef.current = mapMode;
  }, [mapMode]);

  const cycleMode = () => {
    setMapMode((m) => (m === 'follow' ? 'city' : 'follow'));
  };

  // When the user drags out of follow mode we transition to city mode without
  // re-fitting (so the user's pan stands). This ref tells applyMode to skip
  // the next fit on the upcoming mode-change effect.
  const skipNextCityFitRef = useRef(false);

  // Single source of truth for "what city are we in right now?". Drives both
  // the bottom-left label and the city-mode auto-fit.
  const containingCity = useContainingCity(geo.position);

  // Lifetime + per-city progress, refetched on every WS event so they stay live.
  const lifetimeQuery = useQuery({
    queryKey: ['stats', 'lifetime'],
    queryFn: fetchLifetimeStats,
    staleTime: 30_000,
  });
  const progressQuery = useQuery({
    queryKey: ['progress'],
    queryFn: fetchAllProgress,
    staleTime: 30_000,
  });

  const currentCityProgress = containingCity
    ? progressQuery.data?.find((p) => p.city_id === containingCity.city_id) ?? null
    : null;

  const queryClient = useQueryClient();

  // Live cell updates via WebSocket
  const { status: wsStatus } = useRealtimeCells((newCells) => {
    appendCells(newCells);
    if (newCells.length > 0) {
      queryClient.invalidateQueries({ queryKey: ['stats'] });
      queryClient.invalidateQueries({ queryKey: ['progress'] });
    }
  });

  // Bump a version each time WS transitions into 'connected' so the bbox
  // fetch re-runs and we catch up on anything missed during the disconnect.
  useEffect(() => {
    if (wsStatus === 'connected') {
      if (wasConnectedRef.current === false) {
        setReconnectVersion((v) => v + 1);
      }
      wasConnectedRef.current = true;
    } else {
      wasConnectedRef.current = false;
    }
  }, [wsStatus]);

  // Initialize map once
  useEffect(() => {
    if (!containerRef.current) return;
    const map = new maplibregl.Map({
      container: containerRef.current,
      style: config.mapStyleUrl,
      center: DEFAULT_CENTER,
      zoom: DEFAULT_ZOOM,
      attributionControl: { compact: true },
      // No nav controls — Tesla-clean
    });
    mapRef.current = map;

    map.on('load', () => {
      // Boost text legibility on all label (symbol) layers from the base
      // style. Tesla in-car readability beats stylistic subtlety: white text
      // with a strong black halo, and a small size bump on place labels
      // (cities/towns/suburbs) so the names you actually care about pop.
      const styleLayers = map.getStyle()?.layers ?? [];
      for (const layer of styleLayers) {
        if (layer.type !== 'symbol') continue;
        try {
          map.setPaintProperty(layer.id, 'text-color', '#ffffff');
          map.setPaintProperty(layer.id, 'text-halo-color', 'rgba(0, 0, 0, 0.85)');
          map.setPaintProperty(layer.id, 'text-halo-width', 1.6);
        } catch {
          // Layer may be icon-only with no text-* paint — ignore.
        }
        const id = layer.id.toLowerCase();
        const isPlace = id.includes('place') || id.includes('city') || id.includes('town');
        if (!isPlace) continue;
        try {
          const layout = layer.layout as { 'text-size'?: unknown } | undefined;
          const existing = layout?.['text-size'];
          // Multiply whatever scale the style had by 1.25 so we keep its
          // zoom-based interpolation intact instead of overriding to a flat value.
          if (existing !== undefined) {
            map.setLayoutProperty(layer.id, 'text-size', [
              '*',
              existing as maplibregl.ExpressionSpecification | number,
              1.25,
            ]);
          }
        } catch {
          // Layout property doesn't exist or expression couldn't be wrapped — ignore.
        }
      }

      map.addSource(CELL_SOURCE, { type: 'geojson', data: EMPTY_FC });
      map.addSource(PENDING_SOURCE, { type: 'geojson', data: EMPTY_FC });
      map.addSource(CITY_SOURCE, { type: 'geojson', data: EMPTY_FC });

      // Two-pass city boundary: a wide cyan glow underneath for presence at
      // a glance, then a sharp brighter white line on top for crisp definition.
      // Both layers read `is_current` from each feature's properties so the
      // city you're inside renders at full brightness while neighbors dim.
      map.addLayer({
        id: 'city-boundaries-glow',
        type: 'line',
        source: CITY_SOURCE,
        paint: {
          'line-color': '#aee0ff',
          'line-width': ['case', ['get', 'is_current'], 5, 0],
          'line-blur': 4,
          'line-opacity': ['case', ['get', 'is_current'], 0.25, 0],
        },
      });

      map.addLayer({
        id: 'city-boundaries-line',
        type: 'line',
        source: CITY_SOURCE,
        paint: {
          'line-color': [
            'case',
            ['get', 'is_current'],
            'rgba(255, 255, 255, 0.7)',
            'rgba(255, 255, 255, 0.18)',
          ],
          'line-width': ['case', ['get', 'is_current'], 1.5, 1],
        },
      });

      // Pending tiles render UNDER confirmed so confirmed always wins visually
      // when both sets contain the same cell during a brief race. Same cyan
      // family as confirmed but much fainter + dashed border so the
      // "in-progress" state reads as a ghost of the real thing.
      map.addLayer({
        id: 'pending-cells-fill',
        type: 'fill',
        source: PENDING_SOURCE,
        paint: {
          'fill-color': '#aee0ff',
          'fill-opacity': 0.10,
        },
      });

      map.addLayer({
        id: 'pending-cells-line',
        type: 'line',
        source: PENDING_SOURCE,
        paint: {
          'line-color': 'rgba(174, 224, 255, 0.4)',
          'line-width': 0.5,
          'line-dasharray': [3, 2],
        },
      });

      map.addLayer({
        id: 'visited-cells-fill',
        type: 'fill',
        source: CELL_SOURCE,
        paint: {
          'fill-color': '#aee0ff',
          'fill-opacity': 0.3,
        },
      });

      map.addLayer({
        id: 'visited-cells-line',
        type: 'line',
        source: CELL_SOURCE,
        paint: {
          'line-color': 'rgba(174, 224, 255, 0.55)',
          'line-width': 0.5,
        },
      });

      // Restore any persisted pending cells from a previous drive.
      if (pendingCellsRef.current.size > 0) {
        const psrc = map.getSource(PENDING_SOURCE);
        if (psrc && 'setData' in psrc) {
          (psrc as maplibregl.GeoJSONSource).setData(
            cellsToFeatureCollection(pendingCellsRef.current),
          );
        }
      }

      setMapReady(true);
      handleMoveEnd();
    });

    const handleMoveEnd = () => {
      const b = map.getBounds();
      const next: Bbox = {
        min_lat: b.getSouth(),
        min_lng: b.getWest(),
        max_lat: b.getNorth(),
        max_lng: b.getEast(),
      };
      setBbox((prev) => {
        if (
          prev &&
          Math.abs(prev.min_lat - next.min_lat) < 0.001 &&
          Math.abs(prev.min_lng - next.min_lng) < 0.001 &&
          Math.abs(prev.max_lat - next.max_lat) < 0.001 &&
          Math.abs(prev.max_lng - next.max_lng) < 0.001
        ) {
          return prev;
        }
        return next;
      });
    };

    let debounceId: ReturnType<typeof setTimeout> | null = null;
    const onMoveEnd = () => {
      if (debounceId) clearTimeout(debounceId);
      debounceId = setTimeout(handleMoveEnd, VIEWPORT_DEBOUNCE_MS);
    };
    map.on('moveend', onMoveEnd);

    return () => {
      if (debounceId) clearTimeout(debounceId);
      map.remove();
      mapRef.current = null;
    };
  }, []);

  // Drive the camera off (mode, GPS, currentCity). Overview/follow re-center
  // on every GPS update; city mode only re-fits when the user crosses into a
  // new city (or first enters city mode), so panning within a city is honored.
  const lastFitCityIdRef = useRef<string | null>(null);
  const lastAppliedModeRef = useRef<MapMode | null>(null);

  useEffect(() => {
    if (!mapReady) return;
    const map = mapRef.current;
    if (!map) return;

    const modeJustChanged = lastAppliedModeRef.current !== mapMode;
    lastAppliedModeRef.current = mapMode;

    if (mapMode === 'follow') {
      if (geo.position) {
        const zoom = userZoomOverrideRef.current ?? FOLLOW_DEFAULT_ZOOM;
        map.easeTo({
          center: [geo.position.longitude, geo.position.latitude],
          zoom,
          duration: 500,
        });
      }
      lastFitCityIdRef.current = null;
      return;
    }

    // mode === 'city'. Fit the current city's bbox to the screen. While the
    // user stays in this city we never re-fit — they're free to pinch zoom
    // and pan around the city without the camera fighting them. We only
    // refit when they cross into a *different* tracked city.
    const cityId = containingCity?.city_id ?? null;
    const cityChanged = lastFitCityIdRef.current !== cityId;

    // When we entered city mode by drag-from-follow, honor the user's pan
    // and skip the initial fit. We still record what city we're in so that
    // a subsequent crossing into a new one triggers a refit normally.
    if (skipNextCityFitRef.current) {
      skipNextCityFitRef.current = false;
      lastFitCityIdRef.current = cityId;
      return;
    }

    if (!modeJustChanged && !cityChanged) return;
    lastFitCityIdRef.current = cityId;

    if (containingCity) {
      map.fitBounds(
        [
          [containingCity.bbox_min_lng, containingCity.bbox_min_lat],
          [containingCity.bbox_max_lng, containingCity.bbox_max_lat],
        ],
        { padding: CITY_FIT_PADDING, duration: 700 },
      );
    }
    // If we're outside any tracked city polygon, leave the camera alone —
    // the user can still pinch/pan freely and the bottom-left label keeps
    // showing the nearest city for context.
  }, [mapMode, mapReady, geo.position, containingCity]);

  // Listen for user-driven map gestures.
  // - dragstart: a real pan. In follow mode, snap back to overview.
  // - zoomend with originalEvent: a real pinch/wheel. In follow mode, persist
  //   the user's zoom so subsequent GPS updates honor it.
  useEffect(() => {
    if (!mapReady) return;
    const map = mapRef.current;
    if (!map) return;

    const onDragStart = () => {
      if (mapModeRef.current === 'follow') {
        // User wants to look around — drop the follow lock and switch to
        // city mode, but skip the auto-fit so their pan stands.
        skipNextCityFitRef.current = true;
        setMapMode('city');
      }
    };

    const onZoomEnd = (e: maplibregl.MapLibreEvent) => {
      const ev = e as { originalEvent?: Event };
      if (!ev.originalEvent) return; // ignore programmatic easeTo/fitBounds
      if (mapModeRef.current === 'follow') {
        userZoomOverrideRef.current = map.getZoom();
      }
    };

    map.on('dragstart', onDragStart);
    map.on('zoomend', onZoomEnd);
    return () => {
      map.off('dragstart', onDragStart);
      map.off('zoomend', onZoomEnd);
    };
  }, [mapReady]);

  // Fetch visited cells + cities for current bbox
  useEffect(() => {
    if (!mapReady || !bbox) return;
    let cancelled = false;

    (async () => {
      const cellsResult = await fetchVisitedCellsInBbox(bbox).catch((err: unknown) => {
        console.warn('[cabin] visited-bbox fetch failed', err);
        return null;
      });
      const citiesResult = await fetchCitiesInBbox(bbox).catch((err: unknown) => {
        console.warn('[cabin] cities/in-bbox fetch failed', err);
        return null;
      });
      if (cancelled) return;

      if (cellsResult) {
        console.debug('[cabin] visited-bbox returned', cellsResult.length, 'cells');
        appendCells(cellsResult);
      }

      if (citiesResult) {
        citiesRef.current = citiesResult;
        renderCityBoundaries();
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [bbox, mapReady, reconnectVersion]);

  // Re-render cells layer when set changes. Also drains the pending set —
  // any cell that's now confirmed by the backend gets removed from pending.
  function appendCells(newCells: string[]) {
    if (newCells.length === 0) return;
    let added = 0;
    let drained = 0;
    for (const c of newCells) {
      if (!cellsRef.current.has(c)) {
        cellsRef.current.add(c);
        added++;
      }
      if (pendingCellsRef.current.delete(c)) {
        drained++;
      }
    }

    const map = mapRef.current;
    if (!map) return;

    if (added > 0) {
      const src = map.getSource(CELL_SOURCE);
      if (src && 'setData' in src) {
        (src as maplibregl.GeoJSONSource).setData(cellsToFeatureCollection(cellsRef.current));
      }
    }

    if (drained > 0) {
      savePendingToStorage(pendingCellsRef.current);
      const psrc = map.getSource(PENDING_SOURCE);
      if (psrc && 'setData' in psrc) {
        (psrc as maplibregl.GeoJSONSource).setData(
          cellsToFeatureCollection(pendingCellsRef.current),
        );
      }
    }
  }

  // Add a single GPS-derived cell to the pending set, unless it's already
  // confirmed by the backend. Re-renders the pending layer + persists.
  function addPendingFromGps(lat: number, lng: number) {
    const cell = latLngToCell(lat, lng, H3_RES);
    if (cellsRef.current.has(cell)) return;
    if (pendingCellsRef.current.has(cell)) return;
    pendingCellsRef.current.add(cell);
    savePendingToStorage(pendingCellsRef.current);
    const map = mapRef.current;
    if (!map) return;
    const psrc = map.getSource(PENDING_SOURCE);
    if (psrc && 'setData' in psrc) {
      (psrc as maplibregl.GeoJSONSource).setData(
        cellsToFeatureCollection(pendingCellsRef.current),
      );
    }
  }

  // Build the city-boundaries GeoJSON from the cached cities array, tagging
  // each feature with `is_current` so the layer paint can highlight the
  // city you're currently inside while dimming the others.
  function renderCityBoundaries() {
    const map = mapRef.current;
    if (!map) return;
    const currentId = containingCity?.city_id ?? null;
    const fc: FeatureCollection<Polygon | MultiPolygon> = {
      type: 'FeatureCollection',
      features: citiesRef.current.map(
        (c): Feature<Polygon | MultiPolygon> => ({
          type: 'Feature',
          properties: {
            city_id: c.city_id,
            name: c.name,
            is_current: currentId !== null && c.city_id === currentId,
          },
          geometry: c.geometry,
        }),
      ),
    };
    const src = map.getSource(CITY_SOURCE);
    if (src && 'setData' in src) {
      (src as maplibregl.GeoJSONSource).setData(fc);
    }
  }

  // Re-render boundaries when the current city changes, without refetching
  // the bbox. Crossing into a new city flips which polygon is bright.
  useEffect(() => {
    if (!mapReady) return;
    renderCityBoundaries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [containingCity?.city_id, mapReady]);

  // Render the GPS pin as a custom marker
  const markerRef = useRef<maplibregl.Marker | null>(null);
  useEffect(() => {
    if (!mapReady || !geo.position || !mapRef.current) return;
    const lngLat: [number, number] = [geo.position.longitude, geo.position.latitude];
    if (!markerRef.current) {
      const el = document.createElement('div');
      el.className = 'gps-pin';
      el.innerHTML = '<div class="gps-pin-pulse"></div><div class="gps-pin-dot"></div>';
      markerRef.current = new maplibregl.Marker({ element: el, anchor: 'center' })
        .setLngLat(lngLat)
        .addTo(mapRef.current);
    } else {
      markerRef.current.setLngLat(lngLat);
    }
  }, [geo.position, mapReady]);

  // Optimistic pending tiles: every GPS fix adds the containing H3 cell to
  // the pending set (unless already confirmed). Drained back out by
  // appendCells when the phone eventually syncs.
  useEffect(() => {
    if (!mapReady || !geo.position) return;
    addPendingFromGps(geo.position.latitude, geo.position.longitude);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [geo.position, mapReady]);

  return (
    <div className="map-screen">
      <div ref={containerRef} className="map-container" />

      <div className="map-overlay">
        <div className="map-overlay-top">
          <ConnectionPill status={wsStatus} />
          <div className="map-stat">
            <div className="map-stat-row">
              <span className="map-stat-value">
                {(lifetimeQuery.data?.total_cells_visited ?? 0).toLocaleString()}
              </span>
              <span className="map-stat-label">tiles</span>
            </div>
            {currentCityProgress && (
              <div className="map-stat-row map-stat-row-accent">
                <span className="map-stat-value">
                  {currentCityProgress.percentage.toFixed(1)}%
                </span>
                <span className="map-stat-label">{currentCityProgress.city_name}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      <CityLabel city={containingCity} />
      <MapModeButton mode={mapMode} onCycle={cycleMode} />

      {geo.error && !geo.position && <LocationPermissionPrompt error={geo.error} />}
    </div>
  );
}
